import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listDocuments } from '../services/api';
import ClassificationBadge from '../components/ClassificationBadge';
import { DocumentIcon, FunnelIcon } from '@heroicons/react/24/outline';

export default function DocumentsList() {
  const [limit, setLimit] = useState(50);
  const [classificationFilter, setClassificationFilter] = useState('');

  const { data: documentsData, isLoading, error } = useQuery({
    queryKey: ['documents', limit],
    queryFn: () => listDocuments(limit),
    refetchOnWindowFocus: false,
  });

  const filteredDocuments = documentsData?.documents?.filter(doc => {
    if (!classificationFilter) return true;
    return doc.classification?.toUpperCase() === classificationFilter;
  }) || [];

  const getClassificationStats = () => {
    if (!documentsData?.documents) return {};

    const stats = {};
    documentsData.documents.forEach(doc => {
      const classification = doc.classification?.toUpperCase() || 'UNKNOWN';
      stats[classification] = (stats[classification] || 0) + 1;
    });

    return stats;
  };

  const classificationStats = getClassificationStats();

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="section-header">
            <h2 className="!text-2xl">All Documents</h2>
            <span className="control-number">DOC-001</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="metric-card">
              <div className="vault-skeleton h-8 w-16 mx-auto mb-2" />
              <div className="vault-skeleton h-3 w-24 mx-auto" />
            </div>
          ))}
        </div>
        <div className="vault-card p-8 text-center">
          <div className="vault-skeleton h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="vault-card p-4 border-l-2 border-l-[var(--error)]">
          <h3 className="text-sm font-medium text-[var(--error)]">Error Loading Documents</h3>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">
            <p>{error?.response?.data?.detail || 'An error occurred while loading documents.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="section-header">
          <h2 className="!text-2xl">All Documents</h2>
          <span className="control-number">DOC-001</span>
        </div>
        <p className="text-sm text-[var(--text-muted)]">
          Browse and manage all classified documents in the system
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="metric-card">
          <div className="metric-value">{documentsData?.total || 0}</div>
          <div className="metric-label">Total Documents</div>
        </div>

        {Object.entries(classificationStats).map(([classification, count]) => (
          <div key={classification} className="metric-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="metric-value text-xl">{count}</div>
                <div className="metric-label">{classification}</div>
              </div>
              <ClassificationBadge classification={classification} className="text-xs" />
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Controls */}
      <div className="vault-card p-5 mb-6">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="w-5 h-5 text-[var(--spotlight-gold)]" />

          <div>
            <label htmlFor="classification-filter" className="block text-sm font-medium">
              Filter by Classification
            </label>
            <select
              id="classification-filter"
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value)}
              className="mt-1 block w-full"
            >
              <option value="">All Classifications</option>
              {Object.keys(classificationStats).map(classification => (
                <option key={classification} value={classification}>
                  {classification} ({classificationStats[classification]})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="limit" className="block text-sm font-medium">
              Results Limit
            </label>
            <select
              id="limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="mt-1 block w-full"
            >
              <option value={25}>25 documents</option>
              <option value={50}>50 documents</option>
              <option value={100}>100 documents</option>
              <option value={200}>200 documents</option>
            </select>
          </div>

          <div className="flex-1 text-right">
            <p className="text-sm text-[var(--text-muted)]">
              Showing {filteredDocuments.length} of {documentsData?.total || 0} documents
            </p>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="vault-card">
        {filteredDocuments.length > 0 ? (
          <div className="divide-y divide-[var(--vault-border)]">
            {filteredDocuments.map((doc, index) => (
              <div key={doc.id || index} className="px-5 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <DocumentIcon className="w-5 h-5 text-[var(--text-muted)]" />
                      <h3 className="text-base font-medium text-[var(--text-primary)] truncate">
                        {doc.title || 'Untitled Document'}
                      </h3>
                      <ClassificationBadge
                        classification={doc.classification}
                        fullClassification={doc.full_classification}
                        caveats={doc.caveats}
                        releasability={doc.releasability}
                        confidence={doc.confidence}
                      />
                    </div>

                    {doc.content && (
                      <div className="text-sm text-[var(--text-secondary)] mb-3">
                        <p className="line-clamp-2">
                          {doc.content.substring(0, 200)}...
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-6 text-xs text-[var(--text-muted)]">
                      {doc.metadata?.word_count && (
                        <span className="flex items-center">
                          <DocumentIcon className="w-3 h-3 mr-1" />
                          {doc.metadata.word_count} words
                        </span>
                      )}

                      {doc.metadata?.filename && (
                        <span>File: {doc.metadata.filename}</span>
                      )}

                      {doc.metadata?.file_extension && (
                        <span className="uppercase font-medium">
                          {doc.metadata.file_extension}
                        </span>
                      )}

                      {doc.metadata?.extraction_method && (
                        <span>Extracted via: {doc.metadata.extraction_method}</span>
                      )}

                      {doc.timestamp && (
                        <span>Added: {new Date(doc.timestamp).toLocaleDateString()}</span>
                      )}
                    </div>

                    {doc.metadata?.pdf_metadata && (
                      <div className="mt-2 text-xs text-[var(--text-muted)]">
                        <span className="font-medium">PDF Info:</span>
                        {doc.metadata.pdf_metadata.page_count && (
                          <span className="ml-2">{doc.metadata.pdf_metadata.page_count} pages</span>
                        )}
                        {doc.metadata.pdf_metadata.author && (
                          <span className="ml-2">Author: {doc.metadata.pdf_metadata.author}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="ml-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-mono text-[var(--text-primary)]">
                        Score: {(doc.score || 0).toFixed(3)}
                      </div>
                      {doc.confidence && (
                        <div className="text-xs text-[var(--text-muted)]">
                          Confidence: {Math.round(doc.confidence * 100)}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 py-12 text-center">
            <DocumentIcon className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
            <p className="text-base font-medium text-[var(--text-primary)] mb-2">No documents found</p>
            <p className="text-sm text-[var(--text-muted)]">
              {classificationFilter
                ? `No documents found with classification "${classificationFilter}"`
                : 'No documents have been uploaded yet'
              }
            </p>
          </div>
        )}
      </div>

      {documentsData?.total > limit && (
        <div className="mt-6 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            Showing {Math.min(limit, documentsData.total)} of {documentsData.total} documents.
            Increase the limit above to see more results.
          </p>
        </div>
      )}
    </div>
  );
}
